// src/pages/AirbnbRegisterPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Form, Button, Alert, ListGroup } from 'react-bootstrap';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAirbnbAuth } from '../context/AirbnbAuthContext.js';
import { useGoogleMaps } from '../hooks/useGoogleMaps.js';

export default function AirbnbRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAirbnbAuth();
  const { isLoaded, addressService, aucklandBounds } = useGoogleMaps();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [validatingInvite, setValidatingInvite] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteEmail, setInviteEmail] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressDebounceRef = useRef(null);
  const addressSessionTokenRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL;

  // Validate invite token on mount
  useEffect(() => {
    const validateInvite = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('Invalid or missing invite link. Please contact support for an invitation.');
        setValidatingInvite(false);
        setInviteValid(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/airbnb/invites/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (data.valid) {
          setInviteValid(true);
          setInviteToken(token);
          if (data.invite.email) {
            setInviteEmail(data.invite.email);
            setFormData(prev => ({ ...prev, email: data.invite.email }));
          }
        } else {
          setError(data.error || 'Invalid or expired invite link');
          setInviteValid(false);
        }
      } catch (err) {
        setError('Error validating invite. Please try again.');
        setInviteValid(false);
      } finally {
        setValidatingInvite(false);
      }
    };

    validateInvite();
  }, [searchParams, API_URL]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações
    if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.password) {
      setError('All fields are required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register(
      formData.name,
      formData.email,
      formData.phone,
      formData.address,
      formData.password,
      inviteToken
    );

    setLoading(false);

    if (result.success) {
      setSuccess('Registration successful! Redirecting...');
      setTimeout(() => {
        navigate('/airbnb/portal');
      }, 1500);
    } else {
      setError(result.error || 'Registration failed');
    }
  };

  // Show loading while validating invite
  if (validatingInvite) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Validating invite...</span>
        </div>
      </div>
    );
  }

  // Don't show form if invite is invalid
  if (!inviteValid) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        padding: '20px'
      }}>
        <Container style={{ maxWidth: '500px' }}>
          <Card style={{ borderRadius: '15px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <Card.Body style={{ padding: '40px', textAlign: 'center' }}>
              <h3 style={{ color: '#333', marginBottom: '20px' }}>Invalid Invite</h3>
              {error && <Alert variant="danger">{error}</Alert>}
              <p style={{ color: '#666', marginTop: '20px' }}>
                Please contact support to request an invitation.
              </p>
              <Link to="/airbnb/login" style={{ color: '#667eea', fontWeight: '600' }}>
                Back to Login
              </Link>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      padding: '20px'
    }}>
      <Container style={{ maxWidth: '500px' }}>
        <Card style={{ borderRadius: '15px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
          <Card.Body style={{ padding: '40px' }}>
            {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '10px'
              }}>
                It's So Clean
              </h1>
              <p style={{ color: '#666', fontSize: '16px' }}>
                Airbnb Booking Portal
              </p>
            </div>

            <h3 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
              Create Account
            </h3>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  required
                  style={{ padding: '12px', fontSize: '16px' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  readOnly={!!inviteEmail}
                  style={{
                    padding: '12px',
                    fontSize: '16px',
                    backgroundColor: inviteEmail ? '#f0f0f0' : 'white'
                  }}
                />
                {inviteEmail && (
                  <Form.Text className="text-muted">
                    This email is specified in your invitation
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+64 21 123 4567"
                  required
                  style={{ padding: '12px', fontSize: '16px' }}
                />
                <Form.Text className="text-muted">
                  Format: +64 21 123 4567
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" style={{ position: 'relative' }}>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={e => {
                    const val = e.target.value;
                    setFormData({ ...formData, address: val });
                    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
                    if (addressService && val && val.length >= 3) {
                      if (!addressSessionTokenRef.current && window.google?.maps?.places) {
                        addressSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
                      }
                      addressDebounceRef.current = setTimeout(() => {
                        addressService.getPlacePredictions({
                          input: val,
                          componentRestrictions: { country: "nz" },
                          locationRestriction: aucklandBounds,
                          sessionToken: addressSessionTokenRef.current
                        }, (preds, status) => {
                          if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
                            setAddressSuggestions(preds);
                          } else {
                            setAddressSuggestions([]);
                          }
                        });
                      }, 300);
                    } else {
                      setAddressSuggestions([]);
                    }
                  }}
                  placeholder="Start typing your address in Auckland..."
                  required
                  style={{ padding: '12px', fontSize: '16px' }}
                  autoComplete="off"
                />
                {addressSuggestions.length > 0 && (
                  <ListGroup
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginTop: '2px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  >
                    {addressSuggestions.map((pred, idx) => (
                      <ListGroup.Item
                        key={idx}
                        action
                        onClick={() => {
                          setFormData({ ...formData, address: pred.description });
                          setAddressSuggestions([]);
                          addressSessionTokenRef.current = null;
                        }}
                        style={{ cursor: 'pointer', fontSize: '14px' }}
                      >
                        {pred.description}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 6 characters"
                  required
                  style={{ padding: '12px', fontSize: '16px' }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Type password again"
                  required
                  style={{ padding: '12px', fontSize: '16px' }}
                />
              </Form.Group>

              <Button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px'
                }}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </Form>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <p style={{ color: '#666' }}>
                Already have an account?{' '}
                <Link
                  to="/airbnb/login"
                  style={{
                    color: '#667eea',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  Sign In
                </Link>
              </p>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
